import {inject, Injectable, signal} from "@angular/core"
import {Subject} from "rxjs"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_AUTH} from "@common/services/logging"
import {toObservable} from "@angular/core/rxjs-interop"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {maybeEnum} from "@common/helpers/utils/enum"
import {AuthorizationSilo} from "@common/models/auth/authorization-silo"
import {LocalStorageService} from "@common/services/local-storage/local-storage.service"
import {ColormassOrganizationGQL} from "@common/services/upload/upload.generated"
import {AccountType, Membership, MembershipRole, Organization, OrganizationType, SystemRole} from "@generated"
import {MembershipForSiloGQL, OrganizationForSiloGQL} from "@common/services/auth/auth.service.generated"

@Injectable({
    providedIn: "root",
})
export class SiloService {
    readonly $silo = signal<AuthorizationSilo | null | undefined>(undefined)
    silo$ = toObservable(this.$silo)

    // Emits when the staff self-service preview is activated by an explicit switch (not a stored-silo
    // reload), so the starting screen can surface on entering the preview like a real self-service login.
    readonly selfServiceActivated$ = new Subject<void>()

    readonly colormassOrganizationGql = inject(ColormassOrganizationGQL)
    readonly membershipGql = inject(MembershipForSiloGQL)
    readonly organizationGql = inject(OrganizationForSiloGQL)
    readonly localStorageService = inject(LocalStorageService)
    private readonly logger: CmLogger = inject(CM_LOGGER_AUTH)

    constructor() {
        void this.load()
    }

    async load() {
        const {systemRole, organizationId, membershipId, accountType} = this.localStorageService.loadSilo()
        // Client-only self-service preview override (staff only, see side-navigation): forces the active
        // org's accountType so the gated/paid self-service UI can be previewed without a Stripe signup.
        const accountTypeOverride = maybeEnum(accountType, AccountType)

        if (membershipId) {
            // we never want a mismatch between the membership and the organization,
            // so in this case we double-check the organization
            // Cross-org silo-switcher data (org + its group), so fetch outside the silo like the
            // organization branch below.
            const membership = membershipId
                ? await fetchThrowingErrors(this.membershipGql)({id: membershipId}, {context: {withoutSilo: true}}).then(({membership}) => membership)
                : undefined
            this.$silo.set({
                organization: membership?.organization ? {...membership.organization} : undefined,
                membership,
                systemRole: maybeEnum(systemRole, SystemRole),
                label: systemRole ?? membership?.organization?.name ?? "-",
            })
        } else {
            const organization = organizationId
                ? await fetchThrowingErrors(this.organizationGql)({id: organizationId}, {context: {withoutSilo: true}}).then(({organization}) => organization)
                : undefined
            if (accountTypeOverride && organization) {
                this.$silo.set(this.selfServicePreviewSilo(organization, accountTypeOverride))
            } else {
                this.$silo.set({
                    organization: organization ? {...organization} : undefined,
                    systemRole: maybeEnum(systemRole, SystemRole),
                    label: systemRole ?? organization?.name ?? "-",
                })
            }
        }
    }

    activateMembership(
        membership: Pick<Membership, "id" | "role"> & {
            organization: Pick<Organization, "id" | "name" | "url" | "type" | "stripeSubscriptionStatus" | "trialEndsAt">
        },
    ) {
        this.$silo.set({organization: membership.organization, label: membership.organization.name ?? "-", membership})
        this.localStorageService.storeSilo({
            systemRole: null,
            organizationId: null,
            membershipId: membership.id,
            accountType: null,
        })
    }

    activateOrganization(organization: Pick<Organization, "id" | "name" | "url" | "type" | "stripeSubscriptionStatus" | "trialEndsAt">) {
        this.$silo.set({organization: organization, label: organization.name ?? "-"})
        this.localStorageService.storeSilo({
            systemRole: null,
            organizationId: organization.id,
            membershipId: null,
            accountType: null,
        })
    }

    async activateSystemRole(systemRole: SystemRole) {
        try {
            this.localStorageService.storeSilo({
                systemRole,
                organizationId: null,
                membershipId: null,
                accountType: null,
            })
            const {organizations} = await fetchThrowingErrors(this.colormassOrganizationGql)({}, {context: {withoutSilo: true}})
            this.localStorageService.storeSilo({
                organizationId: organizations?.[0]?.id,
            })
            this.$silo.set({systemRole, label: systemRole, organization: organizations?.[0]})
        } catch (error) {
            this.logger.error("Failed to save auth silo to local storage", error)
        }
    }

    // Staff-only preview: browse the colormass organization but with a forced self-service accountType,
    // so the gated (trial) or paid self-service UI can be exercised without a real Stripe signup. The
    // override is client-only — the colormass organization id is still sent for data/scoping.
    async activateSelfService(accountType: AccountType) {
        try {
            const {organizations} = await fetchThrowingErrors(this.colormassOrganizationGql)({}, {context: {withoutSilo: true}})
            const organization = organizations?.[0]
            if (!organization) {
                this.logger.error("Could not resolve the colormass organization for the self-service preview silo")
                return
            }
            this.$silo.set(this.selfServicePreviewSilo(organization, accountType))
            this.localStorageService.storeSilo({
                systemRole: null,
                organizationId: organization.id,
                membershipId: null,
                accountType,
            })
            this.selfServiceActivated$.next()
        } catch (error) {
            this.logger.error("Failed to activate the self-service preview silo", error)
        }
    }

    // Staff self-service preview silo: emulate the self-service account owner on the colormass org. Force
    // EnterpriseCloud type (self-service orgs are auto-created EC) + the chosen accountType, and an Admin
    // membership (self-service signups are auto-created as Admins) so admin-gated pages like organization
    // settings resolve exactly as they would for a real self-service session — no staff special-casing.
    private selfServicePreviewSilo(organization: {id: string}, accountType: AccountType): AuthorizationSilo {
        const previewOrganization = {...organization, type: OrganizationType.EnterpriseCloud, accountType}
        return {
            organization: previewOrganization,
            membership: {id: organization.id, role: MembershipRole.Admin, organization: previewOrganization},
            label: this.selfServiceSiloLabel(accountType),
        }
    }

    private selfServiceSiloLabel(accountType: AccountType): string {
        switch (accountType) {
            case AccountType.SelfServicePaid:
                return "Self-service (paid)"
            case AccountType.SelfServiceFree:
                return "Self-service (free)"
            default:
                return "Self-service (trial)"
        }
    }
}
